import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Wallet, Save } from "lucide-react";
import type { User } from "@/types";
import { SectionCard } from "@/components/SectionCard";
import { Button } from "@/components/ui/button";
import { useSetUserBudget } from "@/api/mutations/budget";
import { useUserBudget } from "@/api/queries/budget";

// #223 UC-5: Budget-Sektion auf der User-Detail-Seite. Monats-Limit des Users
// (= Decke über die Summe seiner Agents) + aktuelle Summe + per-Agent-Aufschlüsselung.
function fmtUsd(n: number): string {
  return `$${n.toFixed(2)}`;
}

export function UserBudgetSection({ user }: { user: User }) {
  const setBudget = useSetUserBudget(user.id);
  const statusQuery = useUserBudget(user.id);
  const status = statusQuery.data;

  const [limit, setLimit] = useState<string>(
    user.budgetPolicy?.monthlyLimitUsd != null ? String(user.budgetPolicy.monthlyLimitUsd) : "",
  );
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    if (!dirty) {
      setLimit(user.budgetPolicy?.monthlyLimitUsd != null ? String(user.budgetPolicy.monthlyLimitUsd) : "");
    }
  }, [user.budgetPolicy, dirty]);

  async function save() {
    const trimmed = limit.trim();
    let value: number | null;
    if (trimmed === "") {
      value = null;
    } else {
      const n = Number(trimmed);
      if (!Number.isFinite(n) || n < 0) {
        toast.error("Limit muss eine Zahl >= 0 sein (oder leer für Plattform-Default)");
        return;
      }
      value = n;
    }
    try {
      await setBudget.mutateAsync(value);
      setDirty(false);
      toast.success("Budget-Limit gespeichert");
    } catch (err) {
      toast.error(`Speichern fehlgeschlagen: ${(err as Error).message}`);
    }
  }

  return (
    <SectionCard
      icon={<Wallet className="h-[18px] w-[18px]" />}
      title="Budget"
      description="Monatliches Kosten-Limit (USD) über alle Agents des Users"
    >
      <div className="space-y-4">
        {status && (
          <>
            <StatTile
              label={`Summe ${status.yearMonth}`}
              value={`${fmtUsd(status.userMtdUsd)} / ${fmtUsd(status.userLimitUsd)}`}
              alert={status.blocked}
            />
            {status.blocked && (
              <div
                className="rounded-md border border-danger-border bg-danger-bg px-3 py-2 text-[12.5px] text-danger"
                data-testid="user-budget-blocked"
              >
                Das User-Budget ist erreicht — ALLE Agents dieses Users pausieren bis Monatswechsel oder Limit-Erhöhung.
              </div>
            )}
            {status.perAgent.length > 0 && (
              <div>
                <div className="mb-1.5 text-[12px] font-semibold text-text2">Pro Agent (dieser Monat)</div>
                <div className="space-y-1.5">
                  {status.perAgent.map((p) => (
                    <div
                      key={p.agentId}
                      className="flex items-center justify-between rounded-md border border-input bg-base px-3 py-1.5"
                    >
                      <span className="truncate text-[13px] text-text">{p.name}</span>
                      <span className="ml-3 shrink-0 font-mono text-[12px] text-text2">
                        {fmtUsd(p.spentUsd)} / {fmtUsd(p.limitUsd)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        <div>
          <label className="mb-1 block text-[12px] font-semibold text-text2">Monats-Limit (USD)</label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={limit}
            onChange={(e) => {
              setLimit(e.target.value);
              setDirty(true);
            }}
            placeholder="leer = Plattform-Default"
            className="flex h-10 w-full rounded-md border border-input bg-base px-3 py-2 text-sm text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-base disabled:cursor-not-allowed disabled:opacity-50"
            disabled={setBudget.isPending}
            data-testid="user-budget-limit-input"
          />
          <span className="mt-1 block text-[11px] text-text3">
            Decke über die Summe ALLER Agents des Users. Leer ⇒ Plattform-Default greift.
          </span>
        </div>

        <div className="flex justify-end">
          <Button size="sm" onClick={save} disabled={!dirty || setBudget.isPending} data-testid="user-budget-save">
            <Save className="h-4 w-4" />
            Speichern
          </Button>
        </div>
      </div>
    </SectionCard>
  );
}

function StatTile({ label, value, alert }: { label: string; value: string; alert: boolean }) {
  return (
    <div className="rounded-md border border-input bg-base px-3 py-2">
      <div className="text-[11px] text-text3">{label}</div>
      <div className={`mt-0.5 font-mono text-[13px] ${alert ? "font-semibold text-danger" : "text-text"}`}>
        {value}
      </div>
    </div>
  );
}
